import { useEffect, useState, useCallback } from "react";
import {
  Plus, Megaphone, Users, MousePointerClick, Coins, Wallet, Clock,
  Check, X, Pencil, ShoppingBag, Settings2, Trophy,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Loader, EmptyState, StatusBadge } from "@/components/common";
import Modal, { TextField, SelectField, TextArea } from "@/components/Modal";
import { PageTitle, Panel } from "./AdminShell";
import { formatFCFA } from "@/lib/format";
import { COMMISSION_STATUS, WITHDRAWAL_STATUS, INFLUENCER_STATUS } from "@/lib/labels";
import { toast } from "sonner";

const TABS = [["dashboard", "Tableau de bord"], ["influencers", "Influenceurs"], ["applications", "Candidatures"],
  ["commissions", "Commissions"], ["withdrawals", "Retraits"], ["settings", "Paramètres"]];

export default function AdminInfluencers() {
  const [tab, setTab] = useState("dashboard");
  return (
    <div>
      <PageTitle>Influenceurs & Parrainage</PageTitle>
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 mb-5 overflow-x-auto no-scrollbar">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`whitespace-nowrap text-sm font-semibold px-4 py-2 rounded-lg transition ${tab === k ? "bg-white shadow text-[#C85A32]" : "text-slate-500"}`} data-testid={`aff-tab-${k}`}>{l}</button>
        ))}
      </div>
      {tab === "dashboard" && <DashboardTab />}
      {tab === "influencers" && <InfluencersTab />}
      {tab === "applications" && <ApplicationsTab />}
      {tab === "commissions" && <CommissionsTab />}
      {tab === "withdrawals" && <WithdrawalsTab />}
      {tab === "settings" && <SettingsTab />}
    </div>
  );
}

function DashboardTab() {
  const [d, setD] = useState(null);
  const [board, setBoard] = useState([]);
  useEffect(() => {
    api.get("/admin/referral/dashboard").then((r) => setD(r.data)).catch(() => setD(false));
    api.get("/admin/influencers").then((r) => setBoard(r.data.filter((i) => i.status === "approved"))).catch(() => {});
  }, []);
  if (!d) return <Loader />;
  const cards = [
    [Users, "Influenceurs actifs", d.total_influencers], [Clock, "Candidatures en attente", d.pending_applications],
    [MousePointerClick, "Clics totaux", d.total_clicks], [Users, "Filleuls", d.total_referrals],
    [ShoppingBag, "Commandes attribuées", d.orders_attributed], [Coins, "Commissions générées", formatFCFA(d.total_commissions)],
    [Wallet, "Total payé", formatFCFA(d.total_paid)], [Clock, "Retraits en attente", formatFCFA(d.pending_withdrawals)],
  ];
  const topReferrals = [...board].sort((a, b) => b.referred - a.referred).slice(0, 5);
  const topRevenue = [...board].sort((a, b) => b.earned - a.earned).slice(0, 5);
  return (
    <div data-testid="aff-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(([Icon, label, value]) => (
          <Panel key={label} className="p-4">
            <Icon className="w-5 h-5 text-[#C85A32]" />
            <p className="text-xl font-display font-extrabold mt-2 text-[#0F172A]">{value}</p>
            <p className="text-xs text-slate-500">{label}</p>
          </Panel>
        ))}
      </div>
      {board.length > 0 && (
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <Leaderboard title="Top par filleuls" icon={Users} rows={topReferrals} valueFn={(i) => `${i.referred} filleul(s)`} testid="board-referrals" />
          <Leaderboard title="Top par revenus" icon={Trophy} rows={topRevenue} valueFn={(i) => formatFCFA(i.earned)} testid="board-revenue" />
        </div>
      )}
    </div>
  );
}

function Leaderboard({ title, icon: Icon, rows, valueFn, testid }) {
  return (
    <Panel className="p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-[#C85A32]" /><h3 className="font-display font-bold text-[#0F172A]">{title}</h3></div>
      <div className="space-y-1.5">
        {rows.map((i, idx) => (
          <div key={i.id} className="flex items-center gap-3 text-sm" data-testid={`${testid}-row-${idx}`}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${idx === 0 ? "bg-[#E08B00] text-white" : "bg-slate-100 text-slate-600"}`}>{idx + 1}</span>
            <span className="flex-1 truncate">{i.name}</span>
            <span className="font-semibold text-slate-700">{valueFn(i)}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function InfluencersTab() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/influencers", { params: { status: "" } }).then((r) => setRows(r.data.filter((i) => i.status !== "pending" && i.status !== "rejected"))), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Loader />;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => setForm(true)} className="bg-[#C85A32] text-white font-bold text-sm px-4 py-2 rounded-lg flex items-center gap-1" data-testid="add-influencer-btn"><Plus className="w-4 h-4" /> Ajouter un influenceur</button>
      </div>
      {rows.length === 0 ? <EmptyState icon={Megaphone} title="Aucun influenceur" subtitle="Ajoutez un influenceur ou approuvez une candidature." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((i) => (
            <Panel key={i.id} className="p-4" data-testid={`influencer-card-${i.id}`}>
              <div className="flex items-start gap-2 mb-2">
                <div className="w-9 h-9 rounded-lg bg-[#0F172A] flex items-center justify-center"><Megaphone className="w-4.5 h-4.5 text-[#E08B00]" style={{ width: 18, height: 18 }} /></div>
                <div className="min-w-0"><p className="font-semibold truncate">{i.name}</p><p className="text-xs text-slate-500 truncate">{i.email}</p></div>
                <StatusBadge map={INFLUENCER_STATUS} value={i.status} className="ml-auto" />
              </div>
              <div className="flex items-center gap-2 text-xs mb-2">
                <span className="font-mono font-bold bg-[#FDF3EE] text-[#C85A32] px-2 py-0.5 rounded">{i.code}</span>
                <span className="text-slate-500">{i.commission_type === "percent" ? `${i.commission_value}%` : formatFCFA(i.commission_value)}</span>
              </div>
              <div className="grid grid-cols-3 gap-1 text-center my-2">
                <Mini label="Filleuls" value={i.referred} /><Mini label="Gains" value={formatFCFA(i.earned)} /><Mini label="Dispo" value={formatFCFA(i.available)} />
              </div>
              <div className="flex gap-2 mt-2">
                <button onClick={() => setEdit(i)} className="flex-1 border border-slate-200 text-slate-700 text-xs font-bold py-1.5 rounded-lg inline-flex items-center justify-center gap-1" data-testid={`influencer-edit-${i.id}`}><Pencil className="w-3.5 h-3.5" /> Modifier</button>
              </div>
            </Panel>
          ))}
        </div>
      )}
      {form && <OnboardModal onClose={() => setForm(null)} onDone={load} />}
      {edit && <EditModal inf={edit} onClose={() => setEdit(null)} onDone={load} />}
    </div>
  );
}

function Mini({ label, value }) {
  return <div className="bg-slate-50 rounded-lg py-1.5"><p className="text-sm font-bold text-[#0F172A]">{value}</p><p className="text-[10px] text-slate-500">{label}</p></div>;
}

function OnboardModal({ onClose, onDone }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", commission_type: "percent", commission_value: 5, social: "" });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/influencers", { ...f, commission_value: Number(f.commission_value) }); toast.success("Influenceur créé"); onDone(); onClose(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal title="Ajouter un influenceur" onClose={onClose} testid="onboard-modal" size="lg">
      <form onSubmit={submit} className="space-y-3">
        <TextField label="Nom complet" value={f.name} onChange={set("name")} required testid="onb-name" />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Email" type="email" value={f.email} onChange={set("email")} required testid="onb-email" />
          <TextField label="Téléphone" value={f.phone} onChange={set("phone")} testid="onb-phone" />
        </div>
        <TextField label="Mot de passe" type="password" value={f.password} onChange={set("password")} required testid="onb-password" />
        <div className="grid sm:grid-cols-2 gap-3">
          <SelectField label="Type de commission" value={f.commission_type} onChange={set("commission_type")} placeholder={null}
            options={[{ value: "percent", label: "Pourcentage (%)" }, { value: "fixed", label: "Montant fixe (FCFA)" }]} testid="onb-ctype" />
          <TextField label={f.commission_type === "percent" ? "Valeur (%)" : "Montant (FCFA)"} type="number" value={String(f.commission_value)} onChange={set("commission_value")} required testid="onb-cvalue" />
        </div>
        <TextField label="Réseaux sociaux" value={f.social} onChange={set("social")} testid="onb-social" />
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="onb-submit">Créer le compte influenceur</button>
      </form>
    </Modal>
  );
}

function EditModal({ inf, onClose, onDone }) {
  const [f, setF] = useState({ commission_type: inf.commission_type, commission_value: inf.commission_value, status: inf.status, phone: inf.phone || "" });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try { await api.put(`/admin/influencers/${inf.id}`, { ...f, commission_value: Number(f.commission_value) }); toast.success("Influenceur mis à jour"); onDone(); onClose(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal title={`Modifier — ${inf.name}`} onClose={onClose} testid="edit-influencer-modal">
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="Type de commission" value={f.commission_type} onChange={set("commission_type")} placeholder={null}
            options={[{ value: "percent", label: "Pourcentage (%)" }, { value: "fixed", label: "Montant fixe" }]} testid="edit-ctype" />
          <TextField label="Valeur" type="number" value={String(f.commission_value)} onChange={set("commission_value")} testid="edit-cvalue" />
        </div>
        <SelectField label="Statut" value={f.status} onChange={set("status")} placeholder={null}
          options={[{ value: "approved", label: "Actif" }, { value: "suspended", label: "Suspendu" }]} testid="edit-status" />
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="edit-submit">Enregistrer</button>
      </form>
    </Modal>
  );
}

function ApplicationsTab() {
  const [rows, setRows] = useState(null);
  const [approve, setApprove] = useState(null);
  const load = useCallback(() => api.get("/admin/influencers", { params: { status: "pending" } }).then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Loader />;
  const reject = async (id) => { try { await api.post(`/admin/influencers/${id}/reject`); toast.success("Candidature refusée"); load(); } catch (e) { toast.error(apiError(e)); } };
  if (rows.length === 0) return <EmptyState icon={Clock} title="Aucune candidature en attente" />;
  return (
    <div className="space-y-3" data-testid="aff-applications">
      {rows.map((a) => (
        <Panel key={a.id} className="p-4" data-testid={`application-${a.id}`}>
          <div className="flex flex-wrap items-start gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-semibold">{a.name}</p>
              <p className="text-xs text-slate-500">{a.email} {a.phone && `· ${a.phone}`}</p>
              {a.social && <p className="text-xs text-slate-500 mt-1">Réseaux : {a.social}</p>}
              {a.audience && <p className="text-xs text-slate-500">Audience : {a.audience}</p>}
              {a.message && <p className="text-sm text-slate-600 mt-1 italic">« {a.message} »</p>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setApprove(a)} className="bg-green-600 text-white text-xs font-bold px-3 py-2 rounded-lg inline-flex items-center gap-1" data-testid={`approve-${a.id}`}><Check className="w-4 h-4" /> Approuver</button>
              <button onClick={() => reject(a.id)} className="border border-red-200 text-red-600 text-xs font-bold px-3 py-2 rounded-lg inline-flex items-center gap-1" data-testid={`reject-${a.id}`}><X className="w-4 h-4" /> Refuser</button>
            </div>
          </div>
        </Panel>
      ))}
      {approve && <ApproveModal app={approve} onClose={() => setApprove(null)} onDone={load} />}
    </div>
  );
}

function ApproveModal({ app, onClose, onDone }) {
  const [f, setF] = useState({ password: "", commission_type: "percent", commission_value: 5 });
  const [result, setResult] = useState(null);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/admin/influencers/${app.id}/approve`, { ...f, commission_value: Number(f.commission_value) });
      setResult(data); toast.success("Candidature approuvée"); onDone();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal title={`Approuver — ${app.name}`} onClose={onClose} testid="approve-modal">
      {result ? (
        <div className="space-y-2 text-sm" data-testid="approve-result">
          <p className="text-green-700 font-semibold">Compte influenceur créé !</p>
          <p>Code de parrainage : <strong className="font-mono">{result.code}</strong></p>
          <p>Mot de passe : <strong className="font-mono">{result.password}</strong></p>
          <p className="text-xs text-slate-500">Ces informations ont été envoyées par email à {app.email}.</p>
          <button onClick={onClose} className="w-full bg-[#C85A32] text-white font-bold py-2.5 rounded-lg mt-2">Fermer</button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <TextField label="Mot de passe (laisser vide = généré)" value={f.password} onChange={set("password")} testid="app-password" />
          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Type de commission" value={f.commission_type} onChange={set("commission_type")} placeholder={null}
              options={[{ value: "percent", label: "Pourcentage (%)" }, { value: "fixed", label: "Montant fixe" }]} testid="app-ctype" />
            <TextField label="Valeur" type="number" value={String(f.commission_value)} onChange={set("commission_value")} testid="app-cvalue" />
          </div>
          <button className="w-full bg-green-600 text-white font-bold py-3 rounded-lg" data-testid="app-approve-submit">Approuver et créer le compte</button>
        </form>
      )}
    </Modal>
  );
}

function CommissionsTab() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admin/commissions").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Loader />;
  const cancel = async (id, status) => { try { await api.put(`/admin/commissions/${id}`, null, { params: { status } }); toast.success("Commission mise à jour"); load(); } catch (e) { toast.error(apiError(e)); } };
  if (rows.length === 0) return <EmptyState icon={Coins} title="Aucune commission" />;
  return (
    <Panel className="overflow-x-auto" data-testid="aff-commissions">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-slate-500 border-b border-slate-200">{["Commande", "Influenceur", "Client", "Base", "Commission", "Statut", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold text-xs uppercase">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} className="border-b border-slate-100 last:border-0" data-testid={`commission-${c.id}`}>
              <td className="px-4 py-3">{c.order_number}</td>
              <td className="px-4 py-3">{c.influencer_name}</td>
              <td className="px-4 py-3">{c.client_name}</td>
              <td className="px-4 py-3">{formatFCFA(c.base_amount)}</td>
              <td className="px-4 py-3 font-semibold">{formatFCFA(c.amount)}</td>
              <td className="px-4 py-3"><StatusBadge map={COMMISSION_STATUS} value={c.status} /></td>
              <td className="px-4 py-3">{c.status === "validee"
                ? <button onClick={() => cancel(c.id, "annulee")} className="text-xs text-red-600 font-semibold" data-testid={`cancel-com-${c.id}`}>Annuler</button>
                : <button onClick={() => cancel(c.id, "validee")} className="text-xs text-green-600 font-semibold" data-testid={`restore-com-${c.id}`}>Rétablir</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

function WithdrawalsTab() {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/withdrawals").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Loader />;
  if (rows.length === 0) return <EmptyState icon={Wallet} title="Aucune demande de retrait" />;
  return (
    <div data-testid="aff-withdrawals">
      <Panel className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-500 border-b border-slate-200">{["N°", "Influenceur", "Montant", "Moyen", "Coordonnées", "Statut", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold text-xs uppercase">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((w) => (
              <tr key={w.id} className="border-b border-slate-100 last:border-0" data-testid={`withdrawal-${w.id}`}>
                <td className="px-4 py-3">{w.withdrawal_number}</td>
                <td className="px-4 py-3">{w.influencer_name}</td>
                <td className="px-4 py-3 font-semibold">{formatFCFA(w.amount)}</td>
                <td className="px-4 py-3">{w.method}</td>
                <td className="px-4 py-3 text-slate-500 max-w-[160px] truncate">{w.details || "—"}</td>
                <td className="px-4 py-3"><StatusBadge map={WITHDRAWAL_STATUS} value={w.status} /></td>
                <td className="px-4 py-3"><button onClick={() => setEdit(w)} className="text-xs text-[#C85A32] font-semibold" data-testid={`process-wd-${w.id}`}>Traiter</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
      {edit && <ProcessWithdrawalModal w={edit} onClose={() => setEdit(null)} onDone={load} />}
    </div>
  );
}

function ProcessWithdrawalModal({ w, onClose, onDone }) {
  const [f, setF] = useState({ status: w.status, reference: w.reference || "", note: w.note || "" });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try { await api.put(`/admin/withdrawals/${w.id}`, f); toast.success("Retrait mis à jour"); onDone(); onClose(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal title={`Retrait ${w.withdrawal_number}`} onClose={onClose} testid="process-wd-modal">
      <form onSubmit={submit} className="space-y-3">
        <p className="text-sm text-slate-500">{w.influencer_name} — <strong className="text-[#C85A32]">{formatFCFA(w.amount)}</strong> via {w.method}</p>
        {w.details && <p className="text-sm bg-slate-50 rounded-lg p-2">{w.details}</p>}
        <SelectField label="Statut" value={f.status} onChange={set("status")} placeholder={null}
          options={[{ value: "pending", label: "En attente" }, { value: "approved", label: "Approuvé" }, { value: "paid", label: "Payé" }, { value: "rejected", label: "Refusé" }]} testid="wd-status" />
        <TextField label="Référence de paiement" value={f.reference} onChange={set("reference")} testid="wd-reference" />
        <TextArea label="Note interne" value={f.note} onChange={set("note")} testid="wd-note" />
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="wd-process-submit">Enregistrer</button>
      </form>
    </Modal>
  );
}

function SettingsTab() {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/admin/referral/settings").then((r) => setF(r.data)); }, []);
  if (!f) return <Loader />;
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/referral/settings", {
        default_commission_type: f.default_commission_type, default_commission_value: Number(f.default_commission_value),
        min_withdrawal: Number(f.min_withdrawal), cookie_days: Number(f.cookie_days),
      });
      toast.success("Paramètres enregistrés");
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Panel className="p-6 max-w-lg" data-testid="aff-settings">
      <div className="flex items-center gap-2 mb-4"><Settings2 className="w-5 h-5 text-[#C85A32]" /><h2 className="font-display text-lg font-bold">Paramètres du programme</h2></div>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="Commission par défaut" value={f.default_commission_type} onChange={set("default_commission_type")} placeholder={null}
            options={[{ value: "percent", label: "Pourcentage (%)" }, { value: "fixed", label: "Montant fixe" }]} testid="set-ctype" />
          <TextField label="Valeur par défaut" type="number" value={String(f.default_commission_value)} onChange={set("default_commission_value")} testid="set-cvalue" />
        </div>
        <TextField label="Retrait minimum (FCFA)" type="number" value={String(f.min_withdrawal)} onChange={set("min_withdrawal")} testid="set-minwd" />
        <TextField label="Durée d'attribution du cookie (jours)" type="number" value={String(f.cookie_days)} onChange={set("cookie_days")} testid="set-cookie" />
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="set-submit">Enregistrer</button>
      </form>
    </Panel>
  );
}
